// src/services/challengeEngine.ts

import {
  collection,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { LogEntry } from '../types';
import type { ChallengeWithXP } from '../constants/challenges';

export interface ChallengeResult {
  xp: number;
  completed: boolean;
  challenge: ChallengeWithXP;
}

export interface BackendChallengeProgress {
  id: string;
  key: string;
  title: string;
  description: string;
  metric: string;
  progress: number;
  target: number;
  completed: boolean;
  rewardXp: number;
  updatedAtMs?: number;
  completedAtMs?: number | null;
}

export interface BackendChallengeCompletion {
  id: string;
  key: string;
  title: string;
  description?: string;
  rewardXp: number;
  completedAtMs?: number | null;
}


export interface BackendAchievement {
  id: string;
  key: string;
  title: string;
  description?: string;
  category?: string;
  rewardXp: number;
  unlockedAtMs?: number | null;
}

function toSafeNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeCompletionKey(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .replace(/__all_time$/, '');
}

/**
 * Backend is the only XP authority.
 * Keep legacy exports so older imports do not break.
 */
export async function awardXP(_userId: string, _amount: number): Promise<number> {
  console.warn('[challengeEngine] awardXP is backend-owned and is now a no-op on the client.');
  return 0;
}

/**
 * Legacy shim only.
 * Real challenge completion is now written by backend progression.
 */
export async function completeChallenge(
  _userId: string,
  challenge: ChallengeWithXP,
): Promise<ChallengeResult> {
  console.warn(
    '[challengeEngine] completeChallenge is backend-owned and is now a read-only shim.',
    challenge?.id,
  );

  return {
    xp: 0,
    completed: false,
    challenge,
  };
}

/**
 * Legacy shim only.
 * Catch-driven challenge evaluation now happens in Cloud Functions.
 */
export async function onCatchLogged(
  _userId: string,
  _catchLog: LogEntry,
): Promise<ChallengeResult[]> {
  console.warn('[challengeEngine] onCatchLogged is backend-owned and is now a no-op on the client.');
  return [];
}

export function evaluateChallengesForCatch(): ChallengeResult[] {
  return [];
}

export { evaluateChallengesForCatch as evaluateCatchChallenges };

export function subscribeToChallengeProgress(
  userId: string,
  onChange: (rows: Record<string, BackendChallengeProgress>) => void,
): Unsubscribe {
  const ref = collection(db, 'users', userId, 'challengeProgress');
  const q = query(ref, orderBy('updatedAtMs', 'desc'));

  return onSnapshot(
    q,
    (snap) => {
      const out: Record<string, BackendChallengeProgress> = {};

      snap.forEach((docSnap) => {
        const data = docSnap.data() as any;
        const key = String(data?.key || docSnap.id).trim();

        out[key] = {
          id: docSnap.id,
          key,
          title: String(data?.title || key),
          description: String(data?.description || ''),
          metric: String(data?.metric || ''),
          progress: toSafeNumber(data?.progress, 0),
          target: Math.max(1, toSafeNumber(data?.target, 1)),
          completed: !!data?.completed,
          rewardXp: toSafeNumber(data?.rewardXp ?? data?.rewardXP, 0),
          updatedAtMs: toSafeNumber(data?.updatedAtMs, 0),
          completedAtMs:
            data?.completedAtMs == null ? null : toSafeNumber(data?.completedAtMs, 0),
        };
      });

      onChange(out);
    },
    (error) => {
      console.error('[challengeEngine] subscribeToChallengeProgress failed', error);
      onChange({});
    },
  );
}

export function subscribeToChallengeCompletions(
  userId: string,
  onChange: (rows: Record<string, BackendChallengeCompletion>) => void,
): Unsubscribe {
  const ref = collection(db, 'users', userId, 'challengeCompletions');
  const q = query(ref, orderBy('completedAtMs', 'desc'));

  return onSnapshot(
    q,
    (snap) => {
      const out: Record<string, BackendChallengeCompletion> = {};

      snap.forEach((docSnap) => {
        const data = docSnap.data() as any;
        const key = normalizeCompletionKey(data?.key || docSnap.id);

        out[key] = {
          id: docSnap.id,
          key,
          title: String(data?.title || key),
          description: typeof data?.description === 'string' ? data.description : undefined,
          rewardXp: toSafeNumber(data?.rewardXp ?? data?.rewardXP, 0),
          completedAtMs:
            data?.completedAtMs == null ? null : toSafeNumber(data?.completedAtMs, 0),
        };
      });

      onChange(out);
    },
    (error) => {
      console.error('[challengeEngine] subscribeToChallengeCompletions failed', error);
      onChange({});
    },
  );
}

/**
 * Compatibility alias for older callers that still think in terms of
 * "completedChallenges" instead of backend challengeCompletions.
 */
export function subscribeToCompletedChallenges(
  userId: string,
  onChange: (rows: Record<string, BackendChallengeCompletion>) => void,
): Unsubscribe {
  return subscribeToChallengeCompletions(userId, onChange);
}

export async function getChallengeCompletions(
  userId: string,
): Promise<Record<string, BackendChallengeCompletion>> {
  const ref = collection(db, 'users', userId, 'challengeCompletions');
  const snap = await getDocs(ref);

  const out: Record<string, BackendChallengeCompletion> = {};

  snap.forEach((docSnap) => {
    const data = docSnap.data() as any;
    const key = normalizeCompletionKey(data?.key || docSnap.id);

    out[key] = {
      id: docSnap.id,
      key,
      title: String(data?.title || key),
      description: typeof data?.description === 'string' ? data.description : undefined,
      rewardXp: toSafeNumber(data?.rewardXp ?? data?.rewardXP, 0),
      completedAtMs:
        data?.completedAtMs == null ? null : toSafeNumber(data?.completedAtMs, 0),
    };
  });

  return out;
}

/**
 * Compatibility helper for old UI consumers.
 */
export async function getCompletedChallenges(
  userId: string,
): Promise<Record<string, BackendChallengeCompletion>> {
  return getChallengeCompletions(userId);
}

export function subscribeToAchievements(
  userId: string,
  onChange: (rows: Record<string, BackendAchievement>) => void,
): Unsubscribe {
  const ref = collection(db, 'users', userId, 'achievements');
  const q = query(ref, orderBy('unlockedAtMs', 'desc'));

  return onSnapshot(
    q,
    (snap) => {
      const out: Record<string, BackendAchievement> = {};

      snap.forEach((docSnap) => {
        const data = docSnap.data() as any;
        const key = normalizeCompletionKey(data?.key || docSnap.id);

        out[key] = {
          id: docSnap.id,
          key,
          title: String(data?.title || key),
          description: typeof data?.description === 'string' ? data.description : undefined,
          category: typeof data?.category === 'string' ? data.category : undefined,
          rewardXp: toSafeNumber(data?.rewardXp ?? data?.rewardXP, 0),
          unlockedAtMs:
            data?.unlockedAtMs == null ? null : toSafeNumber(data?.unlockedAtMs, 0),
        };
      });

      onChange(out);
    },
    (error) => {
      console.error('[challengeEngine] subscribeToAchievements failed', error);
      onChange({});
    },
  );
}

export async function getAchievements(
  userId: string,
): Promise<Record<string, BackendAchievement>> {
  const ref = collection(db, 'users', userId, 'achievements');
  const snap = await getDocs(ref);

  const out: Record<string, BackendAchievement> = {};

  snap.forEach((docSnap) => {
    const data = docSnap.data() as any;
    const key = normalizeCompletionKey(data?.key || docSnap.id);

    out[key] = {
      id: docSnap.id,
      key,
      title: String(data?.title || key),
      description: typeof data?.description === 'string' ? data.description : undefined,
      category: typeof data?.category === 'string' ? data.category : undefined,
      rewardXp: toSafeNumber(data?.rewardXp ?? data?.rewardXP, 0),
      unlockedAtMs:
        data?.unlockedAtMs == null ? null : toSafeNumber(data?.unlockedAtMs, 0),
    };
  });

  return out;
}
